import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Warning, GasPump, CashRegister, Wrench, Checks, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageHeader } from "../components/Shell";
import StatTile from "../components/StatTile";
import { api, currency, FUEL_LABELS, FUEL_COLORS, todayKey, periodKeyFor } from "../lib/api";
import { useStation } from "../lib/station";

export default function Dashboard() {
  const { activeId } = useStation();
  const [tanks, setTanks] = useState([]);
  const [pl, setPl] = useState({ trend: [], by_type: [] });
  const [equipment, setEquipment] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sendingReminders, setSendingReminders] = useState(false);

  useEffect(() => {
    if (!activeId) return;
    Promise.all([
      api.listTanks(activeId),
      api.plSummary(activeId, 7),
      api.listEquipment(activeId),
      api.listChecklistCompletions(activeId),
      api.listChecklistTemplates(activeId),
    ]).then(([t, p, e, c, tpl]) => {
      setTanks(t);
      setPl(p);
      setEquipment(e);
      setCompletions(c);
      setTemplates(tpl);
    });
  }, [activeId]);

  if (!activeId) return null;

  const today = todayKey();
  const todayPl = pl.trend.find((d) => d.date === today) || { revenue: 0, cost: 0, profit: 0 };

  const lowTanks = tanks.filter(
    (t) => (t.current_gallons / t.capacity_gallons) * 100 < t.low_threshold_pct
  );
  const overdueEquip = equipment.filter((e) => e.next_service_date && e.next_service_date < today);
  const overdueChecks = templates.filter((tpl) => {
    const pk = periodKeyFor(tpl.frequency, new Date());
    return !completions.find((c) => c.template_id === tpl.id && c.period_key === pk);
  }).length;

  const alerts = lowTanks.length + overdueEquip.length;

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await api.runReminders(activeId);
      if (res.items === 0) {
        toast.info("No items due — nothing to send");
      } else {
        const ok = (res.email?.sent ? "email " : "") + (res.sms?.sent ? "SMS " : "");
        toast.success(
          ok
            ? `Sent ${res.items}-item digest via ${ok.trim()}`
            : `Built ${res.items}-item digest (no provider keys configured — logged only)`
        );
      }
    } catch {
      toast.error("Failed to send reminders");
    } finally {
      setSendingReminders(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Today"
        subtitle={new Date().toLocaleDateString(undefined, {
          weekday: "long",
          month: "long",
          day: "numeric",
        })}
      />

      {alerts > 0 && (
        <div
          className="mb-5 rounded-lg border-l-4 border-l-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 p-3 flex items-center gap-3 pulse-warn"
          data-testid="alerts-banner"
        >
          <Warning size={22} weight="fill" className="text-[#EF4444]" />
          <div className="flex-1 text-sm">
            <div className="font-bold text-white">{alerts} active alerts</div>
            <div className="text-gray-300 text-xs">
              {lowTanks.length} low tanks · {overdueEquip.length} overdue services
            </div>
          </div>
          <button
            onClick={handleSendReminders}
            disabled={sendingReminders}
            data-testid="send-reminders-btn"
            className="h-9 px-3 rounded-lg surface-elevated text-xs font-bold uppercase tracking-wider text-[#FF6B00] hover:bg-white/[0.04] disabled:opacity-60 inline-flex items-center gap-1"
          >
            <BellRinging size={14} weight="bold" />
            Notify
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mb-5">
        <StatTile label="Revenue Today" value={currency(todayPl.revenue)} accent="#F9FAFB" testId="stat-revenue" />
        <StatTile
          label="Profit Today"
          value={currency(todayPl.profit)}
          accent="#FF6B00"
          sub={todayPl.revenue > 0 ? `${((todayPl.profit / todayPl.revenue) * 100).toFixed(1)}% margin` : "—"}
          testId="stat-profit"
        />
      </div>

      <h2 className="font-display font-bold uppercase tracking-wider text-sm text-gray-400 mb-3">Tank Levels</h2>
      <div className="surface rounded-lg p-4 mb-6 flex items-end justify-around gap-4" data-testid="tank-overview">
        {tanks.map((t) => (
          <TankGauge key={t.id} tank={t} />
        ))}
      </div>

      <h2 className="font-display font-bold uppercase tracking-wider text-sm text-gray-400 mb-3">Jump In</h2>
      <div className="grid grid-cols-2 gap-3">
        <QuickLink to="/cash" icon={CashRegister} label="Log Shift" testId="quick-cash" />
        <QuickLink to="/fuel" icon={GasPump} label="Log Delivery" testId="quick-fuel" />
        <QuickLink
          to="/compliance"
          icon={Checks}
          label="Checklist"
          badge={overdueChecks > 0 ? overdueChecks : null}
          testId="quick-compliance"
        />
        <QuickLink
          to="/maintenance"
          icon={Wrench}
          label="Maintenance"
          badge={overdueEquip.length > 0 ? overdueEquip.length : null}
          testId="quick-maintenance"
        />
      </div>

      <h2 className="font-display font-bold uppercase tracking-wider text-sm text-gray-400 mt-7 mb-3">P&L Snapshot</h2>
      <div className="grid gap-3 mb-2">
        {pl.by_type.map((b) => (
          <div key={b.fuel_type} className="surface rounded-lg p-4" data-testid={`pl-card-${b.fuel_type}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: FUEL_COLORS[b.fuel_type] }} />
                <span className="text-sm font-bold uppercase tracking-wider text-gray-300">{FUEL_LABELS[b.fuel_type]}</span>
              </div>
              <span className="font-display font-black text-lg" style={{ color: FUEL_COLORS[b.fuel_type] }}>
                {b.margin_pct.toFixed(1)}%
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
              <div>
                <div className="text-gray-500 uppercase tracking-wider">Rev</div>
                <div className="font-mono text-white">{currency(b.revenue)}</div>
              </div>
              <div>
                <div className="text-gray-500 uppercase tracking-wider">Cost</div>
                <div className="font-mono text-white">{currency(b.cost)}</div>
              </div>
              <div>
                <div className="text-gray-500 uppercase tracking-wider">Profit</div>
                <div className="font-mono text-white">{currency(b.profit)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Link
        to="/pl"
        className="surface rounded-lg p-3 flex items-center justify-between hover:border-white/20 transition-colors"
        data-testid="link-full-pl"
      >
        <span className="text-sm font-bold uppercase tracking-wider text-gray-300">Full P&L Report</span>
        <ArrowRight size={18} weight="bold" className="text-[#FF6B00]" />
      </Link>
    </div>
  );
}

function TankGauge({ tank }) {
  const pct = Math.max(0, Math.min(100, (tank.current_gallons / tank.capacity_gallons) * 100));
  const isCritical = pct < tank.low_threshold_pct;
  const isWarning = pct < tank.low_threshold_pct * 1.5;
  const fill = isCritical ? "#EF4444" : isWarning ? "#F59E0B" : "#10B981";
  return (
    <div className="flex flex-col items-center gap-2" data-testid={`tank-level-${tank.fuel_type}`}>
      <div className="relative w-12 h-32 rounded-full bg-[#0F1115] border border-white/[0.08] overflow-hidden">
        <div className="absolute bottom-0 left-0 right-0 transition-all duration-700" style={{ height: `${pct}%`, backgroundColor: fill }} />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-mono font-bold text-xs text-white mix-blend-difference">{pct.toFixed(0)}%</span>
        </div>
      </div>
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
          {tank.fuel_type === "mid" ? "Mid" : tank.fuel_type}
        </div>
        <div className="font-mono text-[11px] text-gray-300">{Math.round(tank.current_gallons).toLocaleString()}g</div>
      </div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label, badge, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="surface rounded-lg p-4 flex items-center justify-between hover:border-[#FF6B00]/40 transition-colors group"
    >
      <div className="flex items-center gap-3">
        <Icon size={22} weight="bold" className="text-[#FF6B00] group-hover:scale-110 transition-transform" />
        <span className="text-sm font-bold uppercase tracking-wider text-gray-200">{label}</span>
      </div>
      {badge && (
        <span className="h-5 min-w-5 px-1 rounded-full bg-[#EF4444] text-[10px] font-bold grid place-items-center">{badge}</span>
      )}
    </Link>
  );
}
